import { CompanyFromRegGroupTask, ComputerFromRegGroupTask } from 'mbs-ui-shared@store/rmmGroupTasks/model';
import { NotificationPlanRule, NotifyToPlatform } from '../../../index';

export enum TypeOfSetting {
  Global = 'global',
  Company = 'company',
  Computer = 'computer',
  Custom = 'custom'
}

export interface AlertingScanRange {
  type: TypeOfSetting;
  id?: string;
}

export interface CustomAlertingSettings {
  recipient: { rule?: number; type?: number; emails: string[] };
  pushRecipient: { type: NotificationPlanRule; users: string[] };
  pushNotification: { enabled: boolean };
  highCpuUsage: HighCpuUsage;
  highMemoryUsage: HighMemoryUsage;
  noAntivirus: NoAntivirus;
  lowDiskSpace: LowDiskSpace;
  notUpdated: NotUpdated;
  wrongSystemTime: WrongSystemTime;
  smartNotHealthy: SmartNotHealthy;
  connectionLoss: ConnectionLoss;
  highTemperature: HighTemperature;
  softwareChanges: SoftwareChanges;
  hardwareChanges: HardwareChanges;
  snmpDevice: SnmpDevice;
  serviceState: ServiceState;
}

export enum RMMAlertTypes {
  HighCpuUsage = 'highCpuUsage',
  HighTemperature = 'highTemperature',
  HighMemoryUsage = 'highMemoryUsage',
  WrongSystemTime = 'wrongSystemTime',
  SmartNotHealthy = 'smartNotHealthy',
  ConnectionLoss = 'connectionLoss',
  NoAntivirus = 'noAntivirus',
  NotUpdated = 'notUpdated',
  ServiceState = 'serviceState',
  SoftwareChanges = 'softwareChanges',
  HardwareChanges = 'hardwareChanges',
  LowDiskSpace = 'lowDiskSpace',
  SnmpDevice = 'snmpDevice'
}

export enum RMMAlertPushSettings {
  PushRecipient = 'pushRecipient',
  PushNotification = 'pushNotification'
}

export interface HighCpuUsage {
  warningPercent: number;
  alertPercentMin: number;
  alertPercent: number;
  duration: string;
  enabled: boolean;
}

export interface HighMemoryUsage {
  warningPercent: number;
  alertPercentMin: number;
  alertPercent: number;
  duration: string;
  enabled: boolean;
}

export interface NoAntivirus {
  duration: string;
  enabled: boolean;
}

export interface LowDiskSpace {
  warningPercent: number;
  alertPercentMin: number;
  alertPercent: number;
  excludedVolumeLabels: [];
  excludedMountPoints: [];
  excludeBootVolume: boolean;
  excludeRemovableDisks: boolean;
  duration: string;
  enabled: boolean;
}

export interface NotUpdated {
  duration: string;
  enabled: boolean;
}

export interface WrongSystemTime {
  alertWrongSystemTimeDifference: string;
  duration: string;
  enabled: boolean;
}

export interface SmartNotHealthy {
  duration: string;
  enabled: boolean;
}

export interface ConnectionLoss {
  enabled: boolean;
  notifyToPlatform: NotifyToPlatform;
  notifyOnRestore: boolean;
  duration: string;
}

export interface HighTemperature {
  temperatureMin: number;
  alertHighTemperature: number;
  duration: string;
  enabled: boolean;
}

export interface SoftwareChanges {
  excludedNames: [];
  excludedVendor: [];
  enabled: boolean;
}

export interface HardwareChanges {
  excludedNames: string[];
  excludedPNPClasses: string[];
  excludedPNPDeviceIDs: string[];
  excludedManufacturer: [];
  enabled: boolean;
}

export interface SnmpDevice {
  alerts: string[];
  enabled: boolean;
}

export interface ServiceState {
  enabled: boolean;
  duration: string;
  includeServices: IncludeServices[];
}

export const RmmNotificationFormDefaultValues = {
  recipient: {
    type: NotificationPlanRule.None,
    emails: []
  },
  pushNotification: { enabled: false },
  pushRecipient: {
    type: NotificationPlanRule.None,
    users: []
  },
  [RMMAlertTypes.HighCpuUsage]: { enabled: false },
  [RMMAlertTypes.HighMemoryUsage]: { enabled: false },
  [RMMAlertTypes.LowDiskSpace]: { enabled: false },
  [RMMAlertTypes.SmartNotHealthy]: { enabled: false },
  [RMMAlertTypes.NoAntivirus]: { enabled: false },
  [RMMAlertTypes.NotUpdated]: { enabled: false },
  [RMMAlertTypes.SoftwareChanges]: { enabled: false },
  [RMMAlertTypes.HardwareChanges]: { enabled: false },
  [RMMAlertTypes.ConnectionLoss]: { enabled: false },
  [RMMAlertTypes.SnmpDevice]: { enabled: false },
  [RMMAlertTypes.ServiceState]: { enabled: false },
  [RMMAlertTypes.HighTemperature]: { enabled: false },
  [RMMAlertTypes.WrongSystemTime]: { enabled: false }
};

export interface IncludeServices {
  failedRestart: boolean;
  failedStart: boolean;
  serviceDisplayName: string;
  serviceName: string;
  stop: boolean;
}

export enum NotifyTimeTypeTranslationKeys {
  Hours = 'mbs-ui:rmm-custom-alerting.time.hours',
  Minutes = 'mbs-ui:rmm-custom-alerting.time.minutes'
}

export const CustomAlertingDefaultValues = {
  alertPercent: {
    ninety: '90',
    eighty: '80'
  },
  duration: {
    five: '00:05:00',
    fifteen: '00:15:00',
    twenty: '00:20:00'
  },
  hardwareChanges: {
    excludedNames: [
      'redirected',
      'Remote Desktop Keyboard Device',
      'Remote Audio',
      'Microsoft Remote Display Adapter',
      'Remote Desktop Mouse Device'
    ],
    excludedPNPClasses: [
      'AudioEndpoint',
      'DigitalMediaDevices',
      'MEDIA',
      'Miracast',
      'Monitor',
      'Keyboard',
      'Mouse',
      'Ports',
      'Printer',
      'PrintQueue',
      'SoftwareComponent',
      'SoftwareDevice',
      'WSDPrintDevice',
      'Bluetooth',
      'Display',
      'HIDClass',
      'Image',
      'XnaComposite',
      'WPD'
    ],
    excludedPNPDeviceIDs: ['STORAGEVOLUMESNAPSHOT', 'SWDMSDAS', 'SWD\\IP_TUNNEL_VBUS', 'SWD\\SENSORSANDLOCATIONENUM']
  }
};

export interface ProfilePostActions {
  general?: PostAction;
  [RMMAlertTypes.HighCpuUsage]?: PostAction;
  [RMMAlertTypes.HighMemoryUsage]?: PostAction;
  [RMMAlertTypes.LowDiskSpace]?: PostAction;
  [RMMAlertTypes.SmartNotHealthy]?: PostAction;
  [RMMAlertTypes.NoAntivirus]?: PostAction;
  [RMMAlertTypes.NotUpdated]?: PostAction;
  [RMMAlertTypes.SoftwareChanges]?: PostAction;
  [RMMAlertTypes.HardwareChanges]?: PostAction;
  [RMMAlertTypes.ConnectionLoss]?: PostAction;
  [RMMAlertTypes.SnmpDevice]?: PostAction;
  [RMMAlertTypes.ServiceState]?: PostAction;
  [RMMAlertTypes.HighTemperature]?: PostAction;
  [RMMAlertTypes.WrongSystemTime]?: PostAction;
}

export interface PostAction {
  recipient: {
    type: NotificationPlanRule | PorfileRecipientTypes;
    emails: string[];
  };
  pushNotification: {
    enabled: boolean;
  };
  pushRecipient: {
    type: NotificationPlanRule | PorfileRecipientTypes;
    users: string[];
  };
  windowsScriptGuid: string;
  linuxScriptGuid: string;
  macOsScriptGuid: string;
  connectWise?: {
    enabled: boolean;
  };
}

export interface CustomDataForProfileActions {
  type: string;
  postActionPayload: PostAction;
}

export enum PorfileRecipientTypes {
  None = 'None',
  Provider = 'Provider',
  Administrators = 'Administrators',
  Users = 'Users',
  SpecifiedEmails = 'SpecifiedEmails',
  ProviderAndEmails = 'Provider, SpecifiedEmails'
}

export enum NotificationProfileTypes {
  Global = 'Global',
  Company = 'Company',
  Computer = 'Computer',
  Custom = 'Custom'
}

export enum NotificationProfileTypesInNumber {
  Global = 1,
  Company = 2,
  Computer = 3
}

export interface ProfileStatistics {
  totalCount: number;
  hasGlobal: boolean;
  companyCount: number;
  computerCount: number;
  tagCount: number;
}

export interface RmmNotificationProfile {
  id: string;
  name: string;
  profileType: NotificationProfileTypesInNumber;
  created: string;
  lastlastModified: string;
  settingsMetaData: {
    lastModified: string;
    md5: string;
    path: string;
  };
  applyTo: RmmProfileApplyTo;
  notifications: CustomAlertingSettings;
  postActions: ProfilePostActions;
  postActionsInfo: {
    hasConnectWise: boolean;
    hasEmail: boolean;
    hasInApp: boolean;
    hasScript: boolean;
    isCustomized: boolean;
  };
  disabled?: boolean;
  readOnly?: boolean;
}

export interface RmmNotificationProfileList {
  dataCount: number;
  pageCount: number;
  pageNumber: number;
  pageSize: number;
  data: RmmNotificationProfile[];
}

export interface RmmProfileApplyTo {
  allCompanies: boolean;
  companies: CompanyFromRegGroupTask[];
  computers: ComputerFromRegGroupTask[];
  computerTags: number[];
}

export type ProfileIntesectionResolverPayload = {
  hid: string;
  excludedProfiles: string[];
}[];

export type IntersectionProfile = {
  created: string;
  id: string;
  name: string;
  profileType?: NotificationProfileTypesInNumber;
};

export type IntersectionItem = {
  computerName: string;
  hid: string;
  profiles: IntersectionProfile[];
};

export interface RmmNotificationProfileComputerCountList {
  dataCount: number;
  pageCount: number;
  pageNumber: number;
  pageSize: number;
  data: RmmNotificationProfileComputerCount[];
}

export interface RmmNotificationProfileComputerCount {
  id: string;
  computers: { hid: string; computerName: string }[];
}
export const RmmAlertingWindowsAlerts = [RMMAlertTypes.NotUpdated, RMMAlertTypes.NoAntivirus, RMMAlertTypes.HardwareChanges];
export const RmmAlertingGeneralAlerts = [
  RMMAlertTypes.HighCpuUsage,
  RMMAlertTypes.HighMemoryUsage,
  RMMAlertTypes.LowDiskSpace,
  RMMAlertTypes.WrongSystemTime,
  RMMAlertTypes.SmartNotHealthy,
  RMMAlertTypes.ConnectionLoss,
  RMMAlertTypes.SnmpDevice,
  RMMAlertTypes.ServiceState,
  RMMAlertTypes.SoftwareChanges,
  RMMAlertTypes.HighTemperature
];

export type RmmNotificationsQueryParams = {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  profileType?: string;
  postActionType?: string;
};
